import {useEffect, type RefObject} from "react";

/** Width, in pixels, of the gutter zone measured from the right edge. */
const GUTTER_ZONE_WIDTH_PX = 24;

/** Time the pointer must rest in the zone before the scrollbar shows. */
const REVEAL_DELAY_MS = 150;

/** Time after the last scroll event before the scrollbar fades again. */
const SCROLL_LINGER_MS = 900;

/**
 * Reveal a scrollbar only near the right edge of an element.
 * Set data-gutter-hover to true when the pointer is over the gutter zone.
 * Keep it true while the user drags the scrollbar thumb.
 * Ignore touch input, which has no hover concept.
 */
export function useGutterHover(elementRef: RefObject<HTMLElement | null>) {
    useEffect(() => {
        const element = elementRef.current;
        if (!element) return;

        let frameId: number | null = null;
        let isDragging = false;
        let revealTimer: ReturnType<typeof setTimeout> | null = null;
        let scrollTimer: ReturnType<typeof setTimeout> | null = null;
        let isScrolling = false;
        let pointerInZone = false;

        const cancelReveal = () => {
            if (revealTimer !== null) {
                clearTimeout(revealTimer);
                revealTimer = null;
            }
        };

        const setHover = (isHovering: boolean) => {
            if (isHovering) {
                element.dataset.gutterHover = "true";
            } else {
                delete element.dataset.gutterHover;
            }
        };

        const isInGutterZone = (clientX: number) => {
            const rect = element.getBoundingClientRect();
            return clientX >= rect.right - GUTTER_ZONE_WIDTH_PX;
        };

        const handlePointerMove = (event: PointerEvent) => {
            if (event.pointerType === "touch") return;
            if (isDragging) return;

            const clientX = event.clientX;
            if (frameId !== null) return;
            frameId = requestAnimationFrame(() => {
                frameId = null;
                pointerInZone = isInGutterZone(clientX);
                if (!pointerInZone) {
                    cancelReveal();
                    if (!isScrolling) setHover(false);
                    return;
                }
                // A pointer that only passes through the zone must not reveal the bar.
                if (revealTimer === null && element.dataset.gutterHover !== "true") {
                    revealTimer = setTimeout(() => {
                        revealTimer = null;
                        setHover(true);
                    }, REVEAL_DELAY_MS);
                }
            });
        };

        const handlePointerLeave = (event: PointerEvent) => {
            if (event.pointerType === "touch") return;
            if (isDragging) return;
            pointerInZone = false;
            cancelReveal();
            if (!isScrolling) setHover(false);
        };

        // Scrolling shows the bar. It fades again shortly after the scroll stops.
        const handleScroll = () => {
            isScrolling = true;
            setHover(true);
            if (scrollTimer !== null) clearTimeout(scrollTimer);
            scrollTimer = setTimeout(() => {
                scrollTimer = null;
                isScrolling = false;
                if (!isDragging && !pointerInZone) setHover(false);
            }, SCROLL_LINGER_MS);
        };

        const handlePointerDown = (event: PointerEvent) => {
            if (event.pointerType === "touch") return;
            if (!isInGutterZone(event.clientX)) return;
            isDragging = true;
            setHover(true);
        };

        const handlePointerUp = (event: PointerEvent) => {
            if (event.pointerType === "touch") return;
            if (!isDragging) return;
            isDragging = false;
            setHover(isInGutterZone(event.clientX));
        };

        element.addEventListener("pointermove", handlePointerMove);
        element.addEventListener("pointerleave", handlePointerLeave);
        element.addEventListener("pointerdown", handlePointerDown);
        element.addEventListener("scroll", handleScroll, {passive: true});
        window.addEventListener("pointerup", handlePointerUp);

        return () => {
            if (frameId !== null) cancelAnimationFrame(frameId);
            cancelReveal();
            if (scrollTimer !== null) clearTimeout(scrollTimer);
            element.removeEventListener("scroll", handleScroll);
            element.removeEventListener("pointermove", handlePointerMove);
            element.removeEventListener("pointerleave", handlePointerLeave);
            element.removeEventListener("pointerdown", handlePointerDown);
            window.removeEventListener("pointerup", handlePointerUp);
        };
    }, [elementRef]);
}
